import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/admin/AuthContext";

export const Login = () => {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) nav("/admin", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Bem-vindo, Super Admin!");
      nav("/admin", { replace: true });
    } else {
      toast.error(res.error || "Falha no login");
    }
  };

  return (
    <div className="min-h-screen bg-[#120D0D] flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] radial-gold blur-3xl opacity-50" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative glass-gold w-full max-w-md p-8 md:p-10"
        data-testid="login-card"
      >
        <div className="flex flex-col items-center text-center mb-8">
          <div className="relative mb-4">
            <div className="absolute inset-0 radial-gold scale-150 blur-md" />
            <img src="/manga-symbol.png" alt="Grupo Manga" className="relative w-16 h-16 object-contain gold-glow" />
          </div>
          <h1 className="font-display text-2xl font-extrabold text-white">
            GRUPO <span className="text-gold-gradient">MANGA</span>
          </h1>
          <div className="flex items-center gap-1.5 text-xs text-white/40 mt-2 uppercase tracking-[0.2em]">
            <ShieldCheck className="w-3.5 h-3.5 text-[#F5C542]" /> Painel Super Admin
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Usuário / E-mail"
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[#F5C542]/50 transition-colors"
              data-testid="login-email"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type={show ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Senha"
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl pl-11 pr-11 py-3.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[#F5C542]/50 transition-colors"
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full btn-gold font-display font-bold py-3.5 rounded-xl hover:scale-[1.02] transition-transform disabled:opacity-60"
            data-testid="login-submit"
          >
            {loading ? "Entrando..." : "Entrar"}
          </button>
        </form>

        <p className="text-center text-[11px] text-white/30 mt-6">
          Sessão segura • Acesso restrito ao Super Administrador
        </p>
      </motion.div>
    </div>
  );
};
